import React, { useState, useEffect } from 'react'
import './technicalskills.css'

const TechnicalSkills = () => {
  const [selectedCategory, setSelectedCategory] = useState('backend')
  const [autoRotate, setAutoRotate] = useState(true)

  const skillCategories = [
    {
      id: 'languages',
      title: 'Languages',
      color: '#6366f1',
      description: 'Core programming languages for building scalable applications',
      skills: ['Java', 'SQL', 'TypeScript', 'JavaScript']
    },
    {
      id: 'backend',
      title: 'Backend & Architecture',
      color: '#10b981',
      description: 'Enterprise frameworks and patterns for microservices and distributed systems',
      skills: [
        'Spring Boot',
        'Spring Data JPA',
        'Hibernate',
        'Spring Security',
        'Spring Cloud',
        'REST APIs',
        'OpenFeign',
        'Resilience4j'
      ]
    },
    {
      id: 'data',
      title: 'Data & Messaging',
      color: '#ec4899',
      description: 'Databases and messaging systems for data persistence and async communication',
      skills: [
        'MySQL',
        'MongoDB',
        'Redis',
        'RabbitMQ',
        'Firebase'
      ]
    },
    {
      id: 'concepts',
      title: 'Core Concepts',
      color: '#f59e0b',
      description: 'Architectural patterns and design principles for robust systems',
      skills: [
        'Microservices',
        'Distributed Systems',
        'Event-Driven Architecture',
        'System Design',
        'OOP',
        'Design Patterns'
      ]
    },
    {
      id: 'devops',
      title: 'DevOps & Observability',
      color: '#8b5cf6',
      description: 'Containerization, CI/CD pipelines, and production monitoring',
      skills: [
        'Docker',
        'Docker Compose',
        'CI/CD',
        'GitHub Actions',
        'Micrometer',
        'Zipkin'
      ]
    },
    {
      id: 'frontend',
      title: 'Frontend',
      color: '#06b6d4',
      description: 'Modern UI frameworks and styling for web and mobile applications',
      skills: [
        'React.js',
        'React Native',
        'HTML',
        'CSS',
        'Tailwind'
      ]
    },
    {
      id: 'tools',
      title: 'Developer Tools',
      color: '#3b82f6',
      description: 'Essential tools for version control, API testing, and project management',
      skills: [
        'Git',
        'GitHub',
        'Postman',
        'Jira',
        'IntelliJ IDEA',
        'Claude Code'
      ]
    }
  ]

  const selected = skillCategories.find(cat => cat.id === selectedCategory)

  // Auto-rotate through categories
  useEffect(() => {
    if (!autoRotate) return

    const interval = setInterval(() => {
      setSelectedCategory(prevCategory => {
        const currentIndex = skillCategories.findIndex(cat => cat.id === prevCategory)
        const nextIndex = (currentIndex + 1) % skillCategories.length
        return skillCategories[nextIndex].id
      })
    }, 4000) // Change every 4 seconds

    return () => clearInterval(interval)
  }, [autoRotate, skillCategories])

  const handleCategoryClick = (id) => {
    setSelectedCategory(id)
    setAutoRotate(false) // Stop auto-rotation when user clicks
  }

  return (
    <section id="skills" className='card-container'>
      <h5>What I Work With</h5>
      <h2>Technical Skills</h2>

      <div className="container skills__container">
        <div className="skills__layout">
          {/* Category selector - left side */}
          <div className="skills__categories">
            {skillCategories.map((category) => (
              <button
                key={category.id}
                className={`skills__category-btn ${selectedCategory === category.id ? 'skills__category-btn--active' : ''}`}
                onClick={() => handleCategoryClick(category.id)}
                style={{
                  '--category-color': category.color
                }}
              >
                <span className="skills__category-indicator" style={{ backgroundColor: category.color }}></span>
                {category.title}
              </button>
            ))}
          </div>

          {/* Skills display - right side */}
          <div className="skills__display">
            <div className="skills__header">
              <h3 style={{ color: selected.color }}>{selected.title}</h3>
            </div>

            <div className="skills__tags">
              {selected.skills.map((skill, idx) => (
                <span
                  key={idx}
                  className="skills__tag"
                  style={{ borderColor: selected.color }}
                >
                  {skill}
                </span>
              ))}
            </div>

            <div className="skills__use-cases">
              <span className="skills__use-case-label">Expertise in:</span>
              <div className="skills__use-case-items">
                {selected.id === 'backend' && (
                  <>
                    <span className="skills__use-case-item">Microservices Architecture</span>
                    <span className="skills__use-case-item">Service Communication</span>
                    <span className="skills__use-case-item">API Security</span>
                  </>
                )}
                {selected.id === 'data' && (
                  <>
                    <span className="skills__use-case-item">Data Persistence</span>
                    <span className="skills__use-case-item">Async Messaging</span>
                    <span className="skills__use-case-item">Caching Strategies</span>
                  </>
                )}
                {selected.id === 'devops' && (
                  <>
                    <span className="skills__use-case-item">Containerization</span>
                    <span className="skills__use-case-item">Automated Deployment</span>
                    <span className="skills__use-case-item">Observability & Tracing</span>
                  </>
                )}
                {selected.id === 'concepts' && (
                  <>
                    <span className="skills__use-case-item">System Design</span>
                    <span className="skills__use-case-item">Scalability Patterns</span>
                    <span className="skills__use-case-item">Resilience Engineering</span>
                  </>
                )}
                {selected.id === 'languages' && (
                  <>
                    <span className="skills__use-case-item">Type-Safe Development</span>
                    <span className="skills__use-case-item">Full-Stack Programming</span>
                    <span className="skills__use-case-item">Data Querying</span>
                  </>
                )}
                {selected.id === 'frontend' && (
                  <>
                    <span className="skills__use-case-item">Responsive Interfaces</span>
                    <span className="skills__use-case-item">Cross-Platform Apps</span>
                    <span className="skills__use-case-item">Performance Optimization</span>
                  </>
                )}
                {selected.id === 'tools' && (
                  <>
                    <span className="skills__use-case-item">Version Control</span>
                    <span className="skills__use-case-item">API Development</span>
                    <span className="skills__use-case-item">Team Collaboration</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default TechnicalSkills
